import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Grid,
  Button,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArticleTable from '../../components/Blog/ArticleTable';
import SEOHead from '../../components/SEO/SEOHead';
import PageHeader from '../../components/Layout/PageHeader';
import AtomicAnswer from '../../components/SEO/AtomicAnswer';
import FAQSection from '../../components/SEO/FAQSection';
import FinMapleScoreDetail from '../../components/FinMapleScore/FinMapleScoreDetail';
import { useTranslation } from 'react-i18next';

const ComparePage = ({
  seoTitle,
  seoDescription,
  seoKey,
  noIndex,
  canonical,
  routeKey,
  breadcrumbLabel,
  atomicAnswer,
  banks,
  comparisonRows,
  veredicto,
  faqs,
  mayaText,
  relatedLinks,
}) => {
  const { t } = useTranslation();
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: seoTitle,
    description: seoDescription,
    author: { '@type': 'Organization', name: 'FinMaple', url: 'https://www.finmaple.com' },
    datePublished: '2026-02-20',
    dateModified: '2026-02-24',
    publisher: { '@type': 'Organization', name: 'FinMaple', url: 'https://www.finmaple.com', logo: { '@type': 'ImageObject', url: 'https://www.finmaple.com/og-image.jpg' } },
  };

  return (
    <Box sx={{ bgcolor: '#F7F9FC' }}>
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        seoKey={seoKey}
        noIndex={noIndex}
        canonical={canonical}
        routeKey={routeKey}
        schema={schema}
      />

      <PageHeader
        variant="functional"
        title={seoTitle}
        subtitle={t('comparePage.subtitle')}
        breadcrumbs={[
          { label: t('comparePage.breadcrumbCards'), to: '/tarjetas-de-credito' },
          { label: breadcrumbLabel },
        ]}
        maxWidth="lg"
      />

      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>

        <AtomicAnswer text={atomicAnswer} />

        {/* FinMaple Score side by side */}
        <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 4, mb: 3 }}>
          {t('comparePage.scoreCompared')}
        </Typography>

        <Grid container spacing={3} sx={{ mb: 5 }}>
          {banks.map((bank) => (
            <Grid item xs={12} md={banks.length > 2 ? 3 : 6} key={bank.name}>
              <Box
                sx={{
                  bgcolor: '#fff',
                  borderRadius: '14px',
                  p: 3,
                  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                  height: '100%',
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 0.5 }}>
                  {bank.cardName}
                </Typography>
                <Typography variant="caption" sx={{ color: '#5F6B7A', display: 'block', mb: 2 }}>
                  {bank.name}
                </Typography>
                <FinMapleScoreDetail card={bank.cardData} />
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Comparison table */}
        <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 3 }}>
          {t('comparePage.detailedComparison')}
        </Typography>

        <ArticleTable
          headerBg="#F7F9FC"
          columns={[
            { key: 'label', label: t('comparePage.feature'), headerSx: { color: '#1A1A1A', minWidth: 150 } },
            ...banks.map((bank) => ({
              key: bank.name,
              label: bank.name,
              align: 'center',
              headerSx: { color: '#1A1A1A' },
            })),
          ]}
          rows={comparisonRows.map((row) => {
            const obj = { label: row.label };
            banks.forEach((bank, i) => {
              obj[bank.name] = row.values[i];
            });
            return obj;
          })}
        />

        {/* Veredicto box */}
        <Box
          sx={{
            borderLeft: '4px solid #1E3A5F',
            bgcolor: '#fff',
            borderRadius: '0 14px 14px 0',
            p: { xs: 3, md: 4 },
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            mb: 5,
          }}
        >
          <Typography variant="h4" sx={{ color: '#1E3A5F', mb: 1.5 }}>
            {t('comparePage.verdict')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8 }}>
            {veredicto}
          </Typography>
        </Box>

        {/* FAQ */}
        <FAQSection faqs={faqs} />


        {/* Related comparisons */}
        <Typography variant="h4" sx={{ color: '#1A1A1A', mt: 5, mb: 2 }}>
          {t('comparePage.otherComparisons')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 4 }}>
          {relatedLinks.map((link) => (
            <Button
              key={link.to}
              component={RouterLink}
              to={link.to}
              endIcon={<ArrowForwardIcon />}
              sx={{ justifyContent: 'flex-start', color: '#1E3A5F', textTransform: 'none', fontWeight: 600 }}
            >
              {link.label}
            </Button>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default ComparePage;
